import { supabase } from './supabase';

// Email template service for admin management
export class EmailTemplateService {
  
  // Get all email templates
  static async getAllTemplates() {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .order('template_type');
      
      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching email templates:', error);
      return { success: false, error: error.message };
    }
  }

  // Get template by type
  static async getTemplateByType(templateType) {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('template_type', templateType)
        .single();
      
      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching email template:', error);
      return { success: false, error: error.message };
    }
  }

  // Update email template
  static async updateTemplate(templateType, updates) {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('template_type', templateType)
        .select()
        .single();
      
      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error updating email template:', error);
      return { success: false, error: error.message };
    }
  }

  // Create new email template
  static async createTemplate(templateData) {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .insert([templateData])
        .select()
        .single();
      
      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error creating email template:', error);
      return { success: false, error: error.message };
    }
  }

  // Toggle template active status
  static async toggleTemplateStatus(templateType, isActive) {
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .update({ 
          is_active: isActive,
          updated_at: new Date().toISOString()
        })
        .eq('template_type', templateType)
        .select()
        .single();
      
      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error toggling template status:', error);
      return { success: false, error: error.message };
    }
  }

  // Preview template with sample data
  static async previewTemplate(templateType, sampleData = {}) {
    try {
      const templateResult = await this.getTemplateByType(templateType);
      if (!templateResult.success) {
        throw new Error(templateResult.error);
      }

      const template = templateResult.data;
      
      // Get default sample data based on template type
      const defaultSampleData = this.getDefaultSampleData(templateType);
      const mergedData = { ...defaultSampleData, ...sampleData };
      
      // Process the template with sample data
      const processedSubject = this.processTemplate(template.subject_template, mergedData);
      const processedHtml = this.processTemplate(template.html_template, mergedData);
      const processedText = template.text_template ? 
        this.processTemplate(template.text_template, mergedData) : null;
      
      return {
        success: true,
        data: {
          subject: processedSubject,
          html: processedHtml,
          text: processedText,
          template_type: templateType,
          variables: template.variables
        }
      };
    } catch (error) {
      console.error('Error previewing template:', error);
      return { success: false, error: error.message };
    }
  }

  // Process template with data (simple variable replacement)
  static processTemplate(template, data) {
    let processed = template;
    
    // Replace simple variables {{variable}}
    Object.keys(data).forEach(key => {
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
      processed = processed.replace(regex, data[key] || '');
    });
    
    // Handle conditional blocks {{#if condition}}...{{/if}}
    processed = processed.replace(/{{#if\s+(\w+)}}(.*?){{\/if}}/gs, (match, condition, content) => {
      return data[condition] ? content : '';
    });
    
    // Handle unless blocks {{#unless condition}}...{{/unless}}
    processed = processed.replace(/{{#unless\s+(\w+)}}(.*?){{\/unless}}/gs, (match, condition, content) => {
      return !data[condition] ? content : '';
    });
    
    // Handle each loops {{#each array}}...{{/each}}
    processed = processed.replace(/{{#each\s+(\w+)}}(.*?){{\/each}}/gs, (match, arrayName, content) => {
      const array = data[arrayName];
      if (!Array.isArray(array)) return '';
      
      return array.map(item => {
        return content.replace(/{{this}}/g, item);
      }).join('');
    });
    
    return processed;
  }

  // Get default sample data for template types
  static getDefaultSampleData(templateType) {
    const commonData = {
      user_name: 'John Doe',
      full_name: 'John Doe', // Alternative reference to user's full name
      user_email: 'john.doe@example.com',
      dashboard_url: 'https://solisgames.com/library',
      ticket_url: 'https://solisgames.com/admin-contact-management',
      unsubscribe_url: 'https://solisgames.com/unsubscribe'
    };

    const sampleData = {
      welcome: {
        ...commonData,
        header_title: 'Welcome to Solis Games!'
      },
      contact_admin_reply: {
        ...commonData,
        admin_name: 'Sarah Johnson',
        admin_message: 'Thank you for reaching out! I\'ve reviewed your issue and here\'s what we can do to help...',
        ticket_id: '12345',
        subject: 'Game Loading Issue',
        status: 'In Progress'
      },
      contact_status_update: {
        ...commonData,
        subject: 'Game Loading Issue',
        status: 'resolved',
        status_display: 'Resolved',
        status_color: '#059669',
        ticket_id: '12345',
        updated_at: new Date().toLocaleDateString(),
        admin_note: 'We\'ve fixed the server issue that was causing games to load slowly.',
        status_resolved: true
      },
      subscription_confirmation: {
        ...commonData,
        plan_name: 'Premium Gaming',
        plan_price: '9.99',
        next_billing_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString(),
        subscription_id: 'sub_1234567890',
        plan_features: [
          'Unlimited game access',
          'Premium games library',
          'No ads',
          'Priority support'
        ],
        manage_subscription_url: 'https://solisgames.com/subscription-management'
      },
      subscription_payment_success: {
        ...commonData,
        plan_name: 'Premium Gaming',
        amount: '9.99',
        payment_date: new Date().toLocaleDateString(),
        next_billing_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString(),
        payment_method: 'Visa ending in 4242',
        billing_history_url: 'https://solisgames.com/subscription-management'
      },
      subscription_payment_failed: {
        ...commonData,
        plan_name: 'Premium Gaming',
        amount: '9.99',
        payment_date: new Date().toLocaleDateString(),
        payment_method: 'Visa ending in 4242',
        failure_reason: 'Your card was declined',
        retry_date: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString(),
        update_payment_url: 'https://solisgames.com/subscription-management'
      },
      subscription_cancelled: {
        ...commonData,
        plan_name: 'Premium Gaming',
        access_ends_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString(),
        reactivate_url: 'https://solisgames.com/subscription-management',
        feedback_url: 'https://solisgames.com/contact'
      }
    };

    return sampleData[templateType] || commonData;
  }

  // Get template type options for dropdowns
  static getTemplateTypeOptions() {
    return [
      { value: 'welcome', label: 'Welcome Email', description: 'Sent when users register' },
      { value: 'contact_admin_reply', label: 'Support Reply', description: 'Admin replies to support tickets' },
      { value: 'contact_status_update', label: 'Ticket Status Update', description: 'When ticket status changes' },
      { value: 'subscription_confirmation', label: 'Subscription Confirmed', description: 'New subscription activated' },
      { value: 'subscription_payment_success', label: 'Payment Success', description: 'Successful subscription payment' },
      { value: 'subscription_payment_failed', label: 'Payment Failed', description: 'Failed subscription payment' },
      { value: 'subscription_cancelled', label: 'Subscription Cancelled', description: 'When subscription is cancelled' }
    ];
  }

  // Validate template syntax
  static validateTemplate(template) {
    const errors = [];
    
    // Check for unmatched handlebars
    const openBraces = (template.match(/{{/g) || []).length;
    const closeBraces = (template.match(/}}/g) || []).length;
    
    if (openBraces !== closeBraces) {
      errors.push('Unmatched template braces {{ }}');
    }
    
    // Check for unmatched conditional blocks
    const ifBlocks = (template.match(/{{#if/g) || []).length;
    const endIfBlocks = (template.match(/{{\/if}}/g) || []).length;
    
    if (ifBlocks !== endIfBlocks) {
      errors.push('Unmatched {{#if}} blocks');
    }
    
    // Check for unmatched each blocks
    const eachBlocks = (template.match(/{{#each/g) || []).length;
    const endEachBlocks = (template.match(/{{\/each}}/g) || []).length;
    
    if (eachBlocks !== endEachBlocks) {
      errors.push('Unmatched {{#each}} blocks');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Get email statistics
  static async getEmailStats(days = 30) {
    try {
      const { data, error } = await supabase
        .rpc('get_email_stats', { p_days: days });
      
      if (error) throw error;
      return { success: true, data: data[0] || {} };
    } catch (error) {
      console.error('Error fetching email stats:', error);
      return { success: false, error: error.message };
    }
  }
}

export default EmailTemplateService;
